/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchLoanReports } from "../redux/slices/loanReportSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/LoanReport.css";

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

const PendingDisbursal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loans, loading, error } = useSelector((state) => state.loanReport);

  const [filterData, setFilterData] = useState({
    fromDate: "",
    toDate: "",
    loanAccountNumber: "",
    userId: "",
    userName: "",
    loanType: "",
  });

  const [filteredLoans, setFilteredLoans] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchLoanReports());
  }, [dispatch]);

  useEffect(() => {
    // ✅ Default: only approved loans
    setFilteredLoans(loans.filter((loan) => loan.status === "Approved"));
    setCurrentPage(1);
  }, [loans]);

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error}`);
    }
  }, [error]);

  const handleChange = (e) => {
    setFilterData({ ...filterData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const filtered = loans.filter((loan) => {
      // ✅ First: must be approved
      if (loan.status !== "Approved") return false;

      const { fromDate, toDate, loanAccountNumber, userId, userName, loanType } =
        filterData;
      const appliedDate = new Date(loan.loanAppliedDate);

      return (
        (!fromDate || appliedDate >= new Date(fromDate)) &&
        (!toDate || appliedDate <= new Date(toDate)) &&
        (!loanAccountNumber ||
          loan.loanAccountNumber
            ?.toLowerCase()
            .includes(loanAccountNumber.toLowerCase())) &&
        (!userId || loan.userId?.toString() === userId) &&
        (!userName ||
          loan.userName?.toLowerCase().includes(userName.toLowerCase())) && 
        (!loanType || loan.loanType?.toLowerCase() === loanType.toLowerCase())
      );
    });

    setFilteredLoans(filtered);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")} ${String(
      date.getHours()
    ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  const totalPages = Math.ceil(filteredLoans.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentLoans = filteredLoans.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <div className="LoanReport-container">
      {/* ✅ Filter Section */}
      <section className="LoanReport-filterSection">
        <h2 className="LoanReport-title">Pending Disbursal Filter</h2>
        <p className="LoanReport-subtitle">
          Use filters to refine Approved Loans
        </p>
        <form className="LoanReport-form" onSubmit={handleSubmit}>
          <div className="LoanReport-grid">
            <div className="LoanReport-formGroup">
              <label>From Date</label>
              <input type="date" name="fromDate" onChange={handleChange} />
            </div>
            <div className="LoanReport-formGroup">
              <label>To Date</label>
              <input type="date" name="toDate" onChange={handleChange} />
            </div>
            <div className="LoanReport-formGroup">
              <label>Loan Account Number</label>
              <input
                type="text"
                name="loanAccountNumber"
                placeholder="Loan account number"
                onChange={handleChange}
              />
            </div>
            {/* <div className="LoanReport-formGroup">
              <label>Applicant User ID</label>
              <input
                type="text"
                name="userId"
                placeholder="Applicant user id"
                onChange={handleChange}
              />
            </div> */}
            <div className="LoanReport-formGroup">
              <label>Applicant User Name</label>
              <input
                type="text"
                name="userName"
                placeholder="Applicant user name"
                onChange={handleChange}
              />
            </div>
            <div className="LoanReport-formGroup">
              <label>Loan Type</label>
              <select name="loanType" onChange={handleChange}>
                <option value="">All Loan Types</option>
                <option value="Fixed">Fixed</option>
                <option value="Personal">Personal</option>
              </select>
            </div>
          </div>
          <button type="submit" className="LoanReport-submitBtn">
            Apply Filter
          </button>
        </form>
      </section>

      {/* ✅ Loan Table */}
      <section className="LoanReport-detailsSection">
        <div className="LoanReport-tableHeader">
          <div>
            <label>Show </label>
            <select onChange={handleItemsPerPageChange} value={itemsPerPage}>
              {ITEMS_PER_PAGE_OPTIONS.map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
            <span> entries</span>
          </div>
        </div>

        <div className="LoanReport-tableContainer">
          <table className="LoanReport-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Loan ID</th>
                <th>Borrower Name</th>
                <th>Loan Amount</th>
                <th>Interest Amount</th>
                <th>Loan Tenure</th>
                <th>Loan Frequency</th>
                <th>Applied Date</th>
                <th>Approval Date</th>
                <th>Disbursal Date</th>
                <th>Loan Status</th>
                <th>EMI Amount</th>
                <th>Total Payable</th>
                <th>Loan Type</th>
                {/* <th>View EMI</th>
                <th>Loan Card</th> */}
                <th>Disbursement Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="15">Loading...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="15">Error: {error}</td>
                </tr>
              ) : currentLoans.length === 0 ? (
                <tr>
                  <td colSpan="15">No Approved loans available</td>
                </tr>
              ) : (
                currentLoans.map((loan, index) => (
                  <tr key={loan.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>{loan.loanAccountNumber || "N/A"}</td>
                    <td>{loan.userName || "N/A"}</td>
                    <td>{loan.loanAmount || "N/A"}</td>
                    <td>{loan.loanInterest || "N/A"}</td>
                    <td>{loan.loanTenure || "N/A"}</td>
                    <td>{loan.loanRecoveryFrequency || "N/A"}</td>
                    <td>{new Date(loan.loanAppliedDate).toLocaleDateString("en-GB")}</td>
                    <td>{new Date(loan.firstEmiDate).toLocaleDateString("en-GB")}</td>
                    <td>{new Date(loan.disbursalDate).toLocaleDateString("en-GB")}</td>
                    <td>{loan.status || "N/A"}</td>
                    <td>{loan.loanEmiAmount || "N/A"}</td>
                    <td>{loan.loanPayable || "N/A"}</td>
                    <td>{loan.loanType || "N/A"}</td>
                    {/* <td>
                      <button
                        onClick={() => navigate(`/ViewEmiLoanData/${loan.id}`, { state: loan })}
                      >
                        View EMI
                      </button>
                    </td>
                    <td>
                      <button
                        onClick={() => navigate(`/LoanCardPage/${loan.id}`)}
                      >
                        Loan Card
                      </button>
                    </td> */}
                    <td>
                      <button
                        onClick={() =>
                          navigate(`/LoanDisbursement/${loan.id}`, {
                            state: { selectedLoan: loan },
                          })
                        }
                        style={{
                          marginLeft: "6px",
                          background: "#1f8b94ff",
                          color: "#fff",
                          padding: "5px 10px",
                          border: "none",
                          borderRadius: "4px",
                        }}
                      >
                        Disburse Loan
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Pagination Controls */}
        {totalPages > 1 && (
          <div className="LoanReport-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default PendingDisbursal;
